import { useState, useEffect, useMemo } from "react";
import { getStoredUser, getAccessToken } from "@utils/cookies";
import { useRoleTest, ROLE_TEST_META } from "@common/RoleTestContext";

/**
 * Custom hook to get current user info from cookies.
 * When a test role is active (set via the Role Tester tab), returns the real
 * user with `role` and `role_name` overridden so every role-check in the app
 * reflects the selected test role.
 *
 * Returns { user, realUser, isLoading }
 *   user     — effective user (may have overridden role)
 *   realUser — always the actual logged-in user from cookies
 */
export function useAuth() {
  const [realUser, setRealUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { testRoleId, testClinics } = useRoleTest();

  useEffect(() => {
    setRealUser(getStoredUser());
    setIsLoading(false);
  }, []);

  const user = useMemo(() => {
    if (!realUser || !testRoleId || !ROLE_TEST_META[testRoleId]) return realUser;
    const { role, role_name } = ROLE_TEST_META[testRoleId];
    const merged = { ...realUser, role, role_name };
    // Clinic-scoped roles (area_manager=3, practitioner_lead=4, bookkeeper=8)
    // must have a limited clinic list so access filtering works correctly.
    if ([3, 4, 8].includes(role) && testClinics?.length) {
      merged.clinic = testClinics;
    }
    return merged;
  }, [realUser, testRoleId, testClinics]);

  return { user, realUser, isLoading };
}

/**
 * Get user info synchronously from cookies
 * Use this when you don't need reactivity
 */
export function getUser() {
  return getStoredUser();
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return !!getAccessToken();
}

/**
 * Check if user has specific role
 */
export function hasRole(roleName) {
  const user = getUser();
  return user?.role_name === roleName;
}

/**
 * Check if user is organizer
 */
export function isOrganizer() {
  return hasRole("organizer");
}

/**
 * Check if user is admin
 */
export function isAdmin() {
  return hasRole("admin");
}

/**
 * Check if user is area_manager
 */
export function isAreaManager() {
  return hasRole("area_manager");
}

/**
 * Check if user is visitor
 */
export function isVisitor() {
  return hasRole("visitor");
}
